/**
 * GuidedTour Component - Interactive walkthrough for new users
 * Uses react-joyride for step-by-step tours
 */
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Play,
  X,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Target,
  FileText,
  AlertTriangle,
  Users,
  BarChart3,
  ClipboardCheck,
  Sparkles,
} from "lucide-react";

// Tour steps for different user roles
const TOUR_STEPS = {
  bcm_manager: [
    {
      target: '[data-testid="pdca-plan"]',
      title: "PLAN Phase - Strategic Planning",
      content: "Start here! Define your BCM policy, identify stakeholders, configure BIA settings, and assess risks. ISO 22301 Clauses 4-6.",
      icon: Target,
      placement: "right",
    },
    {
      target: '[data-testid="nav-bcm-policy-wizard"]',
      title: "BCM Policy Wizard",
      content: "Create your Business Continuity Policy using our guided 5-step wizard. This establishes the foundation for your BCMS (ISO 5.2).",
      icon: FileText,
      placement: "right",
    },
    {
      target: '[data-testid="nav-stakeholder-register"]',
      title: "Stakeholder Register",
      content: "Document interested parties and their requirements. Essential for ISO 22301 Clause 4.2 compliance.",
      icon: Users,
      placement: "right",
    },
    {
      target: '[data-testid="nav-bia-configuration"]',
      title: "BIA Configuration",
      content: "Configure Impact Definitions (High/Medium/Low/None), authority matrix, and templates. End users will see these definitions when creating BIAs (ISO 8.2.2).",
      icon: Target,
      placement: "right",
    },
    {
      target: '[data-testid="nav-operational-bia"]',
      title: "Operational BIA",
      content: "Analyze business processes and calculate RTO/RPO. Hover over categories to see Impact Definitions set by your BCM Manager (ISO 8.2.2).",
      icon: BarChart3,
      placement: "right",
    },
    {
      target: '[data-testid="nav-risk-assessment"]',
      title: "Risk Assessment",
      content: "Assess risks to your critical processes using our 38-threat library. Includes SPOF tracking (ISO 8.2.3).",
      icon: AlertTriangle,
      placement: "right",
    },
    {
      target: '[data-testid="pdca-do"]',
      title: "DO Phase - Implementation",
      content: "Implement your BCM strategies: training, asset management, recovery plans, and crisis communication. ISO 22301 Clauses 7-8.",
      icon: Sparkles,
      placement: "right",
    },
    {
      target: '[data-testid="pdca-check"]',
      title: "CHECK Phase - Verification",
      content: "Validate your BCMS through exercises, audits, and KPI monitoring. Track compliance with ISO 22301 Clause 9.",
      icon: ClipboardCheck,
      placement: "right",
    },
    {
      target: '[data-testid="nav-compliance-summary"]',
      title: "Compliance Summary Report",
      content: "NEW! View your overall ISO 22301 compliance score with clause-by-clause breakdown. Identify gaps and priority actions.",
      icon: CheckCircle2,
      placement: "right",
    },
    {
      target: '[data-testid="pdca-act"]',
      title: "ACT Phase - Improvement",
      content: "Continually improve through PACA management and management reviews. Close the PDCA loop (ISO 22301 Clause 10).",
      icon: Target,
      placement: "right",
    },
  ],
  end_user: [
    {
      target: '[data-testid="dashboard-stats"]',
      title: "Your Dashboard",
      content: "View your personal BCM stats: training status, BIA assignments, and upcoming exercises.",
      icon: BarChart3,
      placement: "bottom",
    },
    {
      target: '[data-testid="pdca-plan"]',
      title: "View BIA & Risk Data",
      content: "Access Operational BIA and Risk Assessment data. Hover over impact categories to see definitions set by your BCM Manager.",
      icon: Target,
      placement: "right",
    },
    {
      target: '[data-testid="pdca-do"]',
      title: "Training & Recovery Plans",
      content: "Complete your assigned training and review recovery plans for your department.",
      icon: FileText,
      placement: "right",
    },
    {
      target: '[data-testid="pdca-check"]',
      title: "Exercises & Compliance",
      content: "Participate in BCM exercises and view compliance reports.",
      icon: ClipboardCheck,
      placement: "right",
    },
  ],
  business_user: [
    {
      target: '[data-testid="dashboard-stats"]',
      title: "Your Dashboard",
      content: "View your personal BCM stats: training status, BIA assignments, and upcoming exercises.",
      icon: BarChart3,
      placement: "bottom",
    },
    {
      target: '[data-testid="pdca-plan"]',
      title: "View BIA & Risk Data",
      content: "Access Operational BIA and Risk Assessment data. Hover over impact categories to see definitions set by your BCM Manager.",
      icon: Target,
      placement: "right",
    },
    {
      target: '[data-testid="pdca-do"]',
      title: "Training & Recovery Plans",
      content: "Complete your assigned training and review recovery plans for your department.",
      icon: FileText,
      placement: "right",
    },
    {
      target: '[data-testid="pdca-check"]',
      title: "Exercises & Compliance",
      content: "Participate in BCM exercises and view compliance reports.",
      icon: ClipboardCheck,
      placement: "right",
    },
  ],
};

// Tour tooltip component
function TourTooltip({ step, currentStep, totalSteps, onNext, onPrev, onClose, onFinish }) {
  const Icon = step.icon || Target;
  const isLast = currentStep === totalSteps - 1;
  const isFirst = currentStep === 0;

  return (
    <Card className="w-80 shadow-xl border-sky-200 animate-fade-in">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-sky-100 rounded-lg">
              <Icon className="w-5 h-5 text-sky-700" />
            </div>
            <Badge variant="outline" className="text-xs">
              Step {currentStep + 1} of {totalSteps}
            </Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <h4 className="font-semibold text-slate-900 mb-2">{step.title}</h4>
        <p className="text-sm text-slate-600 mb-4">{step.content}</p>
        
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={onPrev}
            disabled={isFirst}
            className="text-slate-500"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          {isLast ? (
            <Button size="sm" onClick={onFinish} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Finish Tour
            </Button>
          ) : (
            <Button size="sm" onClick={onNext} className="bg-sky-700 hover:bg-sky-800">
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
        
        {/* Progress dots */}
        <div className="flex justify-center gap-1 mt-3">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentStep ? "bg-sky-700 w-4" : i < currentStep ? "bg-indigo-300" : "bg-slate-200"
              }`}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Main GuidedTour component
export default function GuidedTour({ onComplete }) {
  const { user } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourCompleted, setTourCompleted] = useState(false);

  const role = user?.role || "process_manager";
  const steps = TOUR_STEPS[role] || TOUR_STEPS.end_user;

  // Platform admin / webadmin is not a tenant end-user and should never see a guided tour.
  const isPlatformAdmin = role === "admin" || user?.is_platform_admin;

  // Check if user has completed the tour before
  useEffect(() => {
    const completed = localStorage.getItem(`tour_completed_${user?.user_id}`);
    if (completed) {
      setTourCompleted(true);
    }
  }, [user]);

  const startTour = () => {
    setCurrentStep(0);
    setIsRunning(true);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      scrollToTarget(steps[currentStep + 1].target);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      scrollToTarget(steps[currentStep - 1].target);
    }
  };

  const handleClose = () => {
    setIsRunning(false);
  };

  const handleFinish = () => {
    setIsRunning(false);
    setTourCompleted(true);
    localStorage.setItem(`tour_completed_${user?.user_id}`, "true");
    if (onComplete) onComplete();
  };

  const scrollToTarget = (selector) => {
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Position tooltip near target
  const getTooltipPosition = () => {
    const step = steps[currentStep];
    const target = document.querySelector(step.target);
    
    if (!target) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    
    const rect = target.getBoundingClientRect();
    const placement = step.placement || "right";
    
    switch (placement) {
      case "right":
        return { top: rect.top + rect.height / 2, left: rect.right + 20, transform: "translateY(-50%)" };
      case "left":
        return { top: rect.top + rect.height / 2, right: window.innerWidth - rect.left + 20, transform: "translateY(-50%)" };
      case "bottom":
        return { top: rect.bottom + 20, left: rect.left + rect.width / 2, transform: "translateX(-50%)" };
      case "top":
        return { bottom: window.innerHeight - rect.top + 20, left: rect.left + rect.width / 2, transform: "translateX(-50%)" };
      default:
        return { top: rect.top, left: rect.right + 20 };
    }
  };

  // Highlight current target - moved useEffect before conditional return
  useEffect(() => {
    if (isRunning) {
      const step = steps[currentStep];
      const target = document.querySelector(step?.target);
      if (target) {
        target.classList.add("tour-highlight");
        return () => target.classList.remove("tour-highlight");
      }
    }
  }, [isRunning, currentStep, steps]);

  // Platform admin never sees the tour / Restart Tour button
  if (isPlatformAdmin) return null;

  // Start Tour Button (shown when tour not running)
  if (!isRunning) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        {!tourCompleted && (
          <Button
            onClick={startTour}
            className="bg-sky-700 hover:bg-sky-800 shadow-lg animate-pulse"
            data-testid="start-tour-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Take a Tour
          </Button>
        )}
        {tourCompleted && (
          <Button
            variant="outline"
            onClick={startTour}
            className="shadow-lg"
            data-testid="restart-tour-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Restart Tour
          </Button>
        )}
      </div>
    );
  }

  const tooltipStyle = getTooltipPosition();

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />
      
      {/* Tooltip */}
      <div className="fixed z-50" style={tooltipStyle}>
        <TourTooltip
          step={steps[currentStep]}
          currentStep={currentStep}
          totalSteps={steps.length}
          onNext={handleNext}
          onPrev={handlePrev}
          onClose={handleClose}
          onFinish={handleFinish}
        />
      </div>
      
      {/* CSS for highlighting */}
      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 45 !important;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3);
          border-radius: 8px;
          animation: tour-pulse 2s infinite;
        }
        @keyframes tour-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3); }
          50% { box-shadow: 0 0 0 8px rgba(99, 102, 241, 0.3), 0 0 30px rgba(99, 102, 241, 0.2); }
        }
      `}</style>
    </>
  );
}

// Export tour steps for use in other components
export { TOUR_STEPS };
